import { createClient } from '@supabase/supabase-js';
import { Database } from '../types/database';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey);

// Auth helpers
export const auth = {
  signUp: async (email: string, password: string, metadata?: Record<string, any>) => {
    return await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata
      }
    });
  },

  signIn: async (email: string, password: string) => {
    return await supabase.auth.signInWithPassword({
      email,
      password
    });
  },

  signInWithGoogle: async (redirectTo?: string) => {
    // Use provided redirect or default to dashboard
    const redirect = redirectTo || '/dashboard';
    return await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}${redirect}`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        }
      }
    });
  },

  signOut: async () => {
    return await supabase.auth.signOut();
  },

  getCurrentUser: () => {
    return supabase.auth.getUser();
  },

  onAuthStateChange: (callback: (event: string, session: any) => void) => {
    return supabase.auth.onAuthStateChange(callback);
  }
};

// Geographic data queries
export const geography = {
  getCountries: async () => {
    return await (supabase
      .from('countries' as any)
      .select('*')
      .order('name') as any);
  },

  getStates: async (countryId: string) => {
    return await (supabase
      .from('states' as any)
      .select('*, country:countries(*)')
      .eq('country_id', countryId)
      .order('name') as any);
  },

  getLGAs: async (stateId: string) => {
    // Try simplified geometry first with higher tolerance for large provinces
    // If that times out, fall back to very aggressive simplification
    let result = await (supabase.rpc as any)('get_lgas_simplified', {
      p_state_id: stateId,
      p_tolerance: 0.005  // Higher tolerance = more simplification = faster
    });
    
    // If timeout (code 57014), try with even more aggressive simplification
    if (result.error && result.error.code === '57014') {
      console.log('First query timed out, trying with higher simplification...');
      result = await (supabase.rpc as any)('get_lgas_simplified', {
        p_state_id: stateId,
        p_tolerance: 0.01  // Very aggressive simplification for large provinces
      });
    }
    
    // Parse GeoJSON geometry from JSONB to standard geometry format
    if (result.data && Array.isArray(result.data)) {
      result.data = result.data.map((lga: any) => ({
        ...lga,
        // Convert JSONB GeoJSON back to geometry object expected by frontend
        geometry: lga.geometry ? JSON.parse(JSON.stringify(lga.geometry)) : null
      }));
    }
    
    return result;
  },

  getCountryByCode: async (code: string) => {
    return await (supabase
      .from('countries' as any)
      .select('*')
      .eq('code', code)
      .single() as any);
  },

  getIncidentHeatmap: async (params: {
    start_date?: string;
    end_date?: string;
    country_id?: string;
    state_id?: string;
    lga_id?: string;
    incident_type?: string;
    severity?: string;
  }) => {
    // @ts-ignore - RPC function not in generated types
    return await supabase.rpc('get_incident_heatmap', params);
  }
};

// Security data queries
export const security = {
  // Get usage for a specific resource type and date
  getUsage: async (userId: string, resourceType: string, date: string) => {
    return await supabase
      .from('usage_tracking' as any) // Using type assertion since table might not be in generated types
      .select('*')
      .eq('user_id', userId)
      .eq('resource_type', resourceType)
      .eq('usage_date', date)
      .single();
  },

  checkReportAccess: async (reportId: string, userId: string) => {
    // @ts-ignore - RPC function not in generated types
    return await supabase.rpc('check_report_access', {
      report_id: reportId,
      user_id: userId,
    });
  },

  incrementUsage: async (userId: string, resourceType: string) => {
    const today = new Date().toISOString().split('T')[0];
    
    // @ts-ignore - RPC function not in generated types
    return await supabase.rpc('increment_usage', {
      user_uuid: userId,
      resource_type_param: resourceType,
      usage_date_param: today
    });
  }
};

// Real-time subscriptions
export const realtime = {
  subscribeToIncidents: (lgaId: string, callback: (payload: any) => void) => {
    return (supabase as any)
      .channel('security_incidents')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'security_incidents',
          filter: `lga_id=eq.${lgaId}`
        },
        callback
      )
      .subscribe();
  },

  subscribeToReports: (lgaId: string, callback: (payload: any) => void) => {
    return (supabase as any)
      .channel('security_reports')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'security_reports',
          filter: `lga_id=eq.${lgaId}`
        },
        callback
      )
      .subscribe();
  }
};

// User management functions
export const users = {
  getUserProfile: async (userId: string) => {
    return await (supabase
      .from('user_profiles' as any)
      .select('*')
      .eq('id', userId)
      .single() as any);
  },

  updateUserProfile: async (userId: string, updates: any) => {
    return await (supabase
      .from('user_profiles' as any)
      .update(updates)
      .eq('id', userId) as any);
  },

  createUserProfile: async (profile: any) => {
    return await (supabase
      .from('user_profiles' as any)
      .insert(profile) as any);
  },

  getUsageTracking: async (userId: string, resourceType: string, date: string) => {
    return await (supabase
      .from('usage_tracking' as any)
      .select('*')
      .eq('user_id', userId)
      .eq('resource_type', resourceType)
      .eq('usage_date', date)
      .single() as any);
  },

  incrementUsage: async (userId: string, resourceType: string) => {
    const today = new Date().toISOString().split('T')[0];
    // @ts-ignore - RPC function not in generated types
    return await supabase.rpc('increment_usage', {
      user_uuid: userId,
      resource_type_param: resourceType,
      usage_date_param: today
    });
  },

  checkReportAccess: async (userId: string, level: string, id: string) => {
    // @ts-ignore - RPC function not in generated types
    return await supabase.rpc('check_report_access', {
      user_id: userId,
      report_level: level,
      report_id: id
    });
  },

  getUserSubscription: async (userId: string) => {
    return await (supabase
      .from('subscriptions' as any)
      .select('*')
      .eq('user_id', userId)
      .single() as any);
  }
};
